import * as THREE from 'three'

function uint8ArrayToBase64(bytes: Uint8Array): string {
  let binary = ''
  const chunkSize = 8192
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const end = Math.min(i + chunkSize, bytes.length)
    binary += String.fromCharCode.apply(null, bytes.subarray(i, end) as unknown as number[])
  }
  return btoa(binary)
}

export interface ExportMeasurements {
  name: string
  dims: string
  volume: string
  surfaceArea: string
  weight: string
}

export interface ExportAnnotation {
  x: number; y: number; z: number; text: string
}

const LOCALE_STRINGS: Record<string, Record<string, string>> = {
  zh: {
    colName: '名称',
    colDims: '尺寸',
    colVolume: '体积',
    colSurface: '表面积',
    colWeight: '重量',
    hint: '鼠标拖拽旋转 · 滚轮缩放 · 右键平移',
  },
  en: {
    colName: 'Name',
    colDims: 'Dimensions',
    colVolume: 'Volume',
    colSurface: 'Surface Area',
    colWeight: 'Weight',
    hint: 'Drag to rotate · Scroll to zoom · Right-click to pan',
  },
  de: {
    colName: 'Name',
    colDims: 'Abmessungen',
    colVolume: 'Volumen',
    colSurface: 'Oberfläche',
    colWeight: 'Gewicht',
    hint: 'Ziehen zum Drehen · Scrollen zum Zoomen · Rechtsklick zum Verschieben',
  },
}

function generateHTML(compressedBase64: string, measurements: ExportMeasurements[], annotations: ExportAnnotation[], locale: string): string {
  const t = LOCALE_STRINGS[locale] || LOCALE_STRINGS.en

  const measRows = measurements.map(m => `
    <tr>
      <td>${escapeHtml(m.name)}</td>
      <td>${escapeHtml(m.dims)}</td>
      <td>${escapeHtml(m.volume)}</td>
      <td>${escapeHtml(m.surfaceArea)}</td>
      <td>${escapeHtml(m.weight)}</td>
    </tr>`).join('')

  return `<!DOCTYPE html>
<html lang="${locale === 'zh' ? 'zh-CN' : locale}">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>3D Model Viewer</title>
<style>
*{margin:0;padding:0;box-sizing:border-box}
body{background:#0f0f1a;overflow:hidden;font-family:'Segoe UI',system-ui,sans-serif}
canvas{display:block;width:100vw;height:100vh}
.info{position:fixed;bottom:16px;left:50%;transform:translateX(-50%);color:#666;font-size:13px;background:rgba(15,15,26,0.8);padding:8px 16px;border-radius:8px;border:1px solid #2a2a3e;pointer-events:none;user-select:none}
.meas-panel{position:fixed;top:12px;right:12px;background:rgba(15,15,26,0.9);border:1px solid #2a2a3e;border-radius:8px;padding:10px 14px;color:#aaa;font-size:12px;max-height:50vh;overflow-y:auto;pointer-events:none;user-select:none}
.meas-panel table{border-collapse:collapse;width:100%}
.meas-panel th,.meas-panel td{padding:3px 8px;text-align:left;border-bottom:1px solid #2a2a3e;white-space:nowrap}
.meas-panel th{color:#888;font-weight:400;font-size:11px}
.meas-panel td{color:#ccc;font-size:11px}
.meas-panel .title{color:#888;font-size:11px;margin-bottom:4px}
</style>
</head>
<body>
${measurements.length > 0 ? `<div class="meas-panel"><table><tr><th>${t.colName}</th><th>${t.colDims}</th><th>${t.colVolume}</th><th>${t.colSurface}</th><th>${t.colWeight}</th></tr>${measRows}</table></div>` : ''}
<div class="info">${t.hint}</div>
<script src="https://unpkg.com/pako@3.0.1/dist/browser/pako_inflate.umd.min.js"><\/script>
<script type="importmap">
{
  "imports": {
    "three": "https://unpkg.com/three@0.160.0/build/three.module.js",
    "three/addons/": "https://unpkg.com/three@0.160.0/examples/jsm/"
  }
}
<\/script>
<script type="module">
import * as THREE from 'three'
import { OrbitControls } from 'three/addons/controls/OrbitControls.js'
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js'

const annotations = ${JSON.stringify(annotations)}

const compressedBase64 = '${compressedBase64}'

const binary = Uint8Array.from(atob(compressedBase64), c => c.charCodeAt(0))
const decompressed = pako.ungzip(binary)
const blob = new Blob([decompressed], {type: 'application/octet-stream'})
const url = URL.createObjectURL(blob)

const scene = new THREE.Scene()
scene.background = new THREE.Color(0x1a1a2e)

const camera = new THREE.PerspectiveCamera(45, window.innerWidth / window.innerHeight, 0.1, 1000)
const renderer = new THREE.WebGLRenderer({ antialias: true })
renderer.setPixelRatio(Math.min(devicePixelRatio, 3))
renderer.toneMapping = THREE.ACESFilmicToneMapping
renderer.toneMappingExposure = 1.0
renderer.outputColorSpace = THREE.SRGBColorSpace
renderer.setSize(window.innerWidth, window.innerHeight)
document.body.appendChild(renderer.domElement)

const controls = new OrbitControls(camera, renderer.domElement)
controls.enableDamping = true
controls.dampingFactor = 0.1
controls.target.set(0, 0, 0)

const ambient = new THREE.AmbientLight(0xffffff, 0.7)
scene.add(ambient)
const hemi = new THREE.HemisphereLight(0xffffff, 0x444444, 0.6)
scene.add(hemi)
const dir = new THREE.DirectionalLight(0xffffff, 1.2)
dir.position.set(10, 15, 10)
dir.castShadow = true
dir.shadow.mapSize.set(1024, 1024)
scene.add(dir)
const fill = new THREE.DirectionalLight(0xffffff, 0.6)
fill.position.set(-10, 8, -10)
scene.add(fill)

const loader = new GLTFLoader()
loader.load(url, gltf => {
  const model = gltf.scene
  model.traverse(c => {
    if (c instanceof THREE.Mesh) {
      c.castShadow = true
      c.receiveShadow = true
    }
  })

  const box = new THREE.Box3().setFromObject(model)
  const size = box.getSize(new THREE.Vector3())
  const maxDim = Math.max(size.x, size.y, size.z)
  if (maxDim > 0) {
    const s = 5 / maxDim
    model.scale.setScalar(s)
  }
  const center = box.getCenter(new THREE.Vector3())
  model.position.set(-center.x * model.scale.x, -center.y * model.scale.y, -center.z * model.scale.z)

  scene.add(model)

  if (annotations.length > 0) {
    const s = maxDim > 0 ? 5 / maxDim : 1
    const offset = new THREE.Vector3(-center.x * s, -center.y * s, -center.z * s)
    for (const ann of annotations) {
      const pos = new THREE.Vector3(ann.x, ann.y, ann.z).multiplyScalar(s).add(offset)
      const sphere = new THREE.Mesh(new THREE.SphereGeometry(0.04, 10, 10), new THREE.MeshBasicMaterial({ color: 0xff3333 }))
      sphere.position.copy(pos)
      scene.add(sphere)
      const canvas2 = document.createElement('canvas')
      const ctx2 = canvas2.getContext('2d')
      const fontSize = 80
      ctx2.font = 'bold ' + fontSize + 'px Consolas, monospace'
      var chars = ann.text.split('')
      var lines = []
      var line = ''
      var maxLineW = 600
      for (var ci = 0; ci < chars.length; ci++) {
        var testLine = line + chars[ci]
        if (ctx2.measureText(testLine).width > maxLineW && line.length > 0) {
          lines.push(line)
          line = chars[ci]
        } else {
          line = testLine
        }
      }
      if (line) lines.push(line)
      if (lines.length === 0) lines = [ann.text]
      var lineH = fontSize * 1.3
      var W2 = Math.min(Math.ceil(maxLineW + 40), 2048)
      var H2 = Math.max(Math.ceil(lines.length * lineH + 20), 128)
      canvas2.width = W2; canvas2.height = H2
      ctx2.clearRect(0, 0, W2, H2)
      ctx2.fillStyle = '#ff3333'
      ctx2.font = 'bold ' + fontSize + 'px Consolas, monospace'
      ctx2.textAlign = 'center'
      ctx2.textBaseline = 'middle'
      var startY2 = H2 / 2 - ((lines.length - 1) * lineH) / 2
      for (var li = 0; li < lines.length; li++) {
        ctx2.fillText(lines[li], W2 / 2, startY2 + li * lineH)
      }
      const tex = new THREE.CanvasTexture(canvas2)
      tex.minFilter = THREE.LinearFilter
      tex.magFilter = THREE.LinearFilter
      const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, depthTest: false, depthWrite: false, sizeAttenuation: true, transparent: true }))
      sprite.position.copy(pos)
      sprite.position.y += 0.08
      var aspect2 = W2 / H2
      sprite.scale.set(0.5 * aspect2, 0.5, 1)
      scene.add(sprite)
    }
  }

  const dist = maxDim > 0 ? maxDim * 1.5 + 3 : 8
  camera.position.set(dist * 0.6, dist * 0.6, dist * 0.6)
  controls.update()
})

  const grid = new THREE.GridHelper(20, 20, 0x444466, 0x222244)
scene.add(grid)

function animate() {
  requestAnimationFrame(animate)
  controls.update()
  renderer.render(scene, camera)
}
animate()

window.addEventListener('resize', () => {
  camera.aspect = window.innerWidth / window.innerHeight
  camera.updateProjectionMatrix()
  renderer.setSize(window.innerWidth, window.innerHeight)
})
<\/script>
</body>
</html>`
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const chars = text.split('')
  const lines: string[] = []
  let line = ''
  for (const char of chars) {
    const testLine = line + char
    if (ctx.measureText(testLine).width > maxWidth && line.length > 0) {
      lines.push(line)
      line = char
    } else {
      line = testLine
    }
  }
  if (line) lines.push(line)
  return lines.length > 0 ? lines : [text]
}

function createTextMesh(text: string, fontSize = 80): THREE.Mesh {
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')!
  const font = 'bold ' + fontSize + 'px Consolas, monospace'
  ctx.font = font
  const maxLineWidth = 1200
  const lines = wrapText(ctx, text, maxLineWidth)
  const lineHeight = fontSize * 1.3
  let maxLinePixelWidth = 0
  for (const l of lines) {
    const w = ctx.measureText(l).width
    if (w > maxLinePixelWidth) maxLinePixelWidth = w
  }
  const W = Math.min(Math.ceil(maxLinePixelWidth + 40), 4096)
  const H = Math.max(Math.ceil(lines.length * lineHeight + 20), 128)
  canvas.width = W
  canvas.height = H
  ctx.clearRect(0, 0, W, H)
  ctx.fillStyle = '#ff3333'
  ctx.font = font
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  const startY = H / 2 - ((lines.length - 1) * lineHeight) / 2
  for (let i = 0; i < lines.length; i++) {
    ctx.fillText(lines[i], W / 2, startY + i * lineHeight)
  }
  const tex = new THREE.CanvasTexture(canvas)
  tex.minFilter = THREE.LinearFilter
  tex.magFilter = THREE.LinearFilter
  const aspect = W / H
  const planeHeight = 0.5
  const planeGeo = new THREE.PlaneGeometry(planeHeight * aspect, planeHeight)
  const planeMat = new THREE.MeshBasicMaterial({ map: tex, transparent: true, side: THREE.DoubleSide, depthTest: false, depthWrite: false })
  const mesh = new THREE.Mesh(planeGeo, planeMat)
  mesh.renderOrder = 999
  return mesh
}

export async function exportModelAsGLB(
  group: THREE.Group,
  annotations: ExportAnnotation[] = [],
  fileName: string = 'annotated-model.glb',
): Promise<void> {
  const [{ GLTFExporter }] = await Promise.all([
    import('three/examples/jsm/exporters/GLTFExporter') as Promise<typeof import('three/examples/jsm/exporters/GLTFExporter')>,
  ])

  const exportGroup = group.clone()

  if (annotations.length > 0) {
    exportGroup.userData.annotations = annotations

    const markerGeo = new THREE.SphereGeometry(0.04, 10, 10)
    const markerMat = new THREE.MeshBasicMaterial({ color: 0xff3333 })

    for (const ann of annotations) {
      const marker = new THREE.Mesh(markerGeo, markerMat.clone())
      marker.position.set(ann.x, ann.y, ann.z)
      marker.userData.annotationText = ann.text
      exportGroup.add(marker)

      const textMesh = createTextMesh(ann.text || `标注 ${annotations.indexOf(ann) + 1}`)
      textMesh.position.set(ann.x, ann.y + 0.3, ann.z)
      textMesh.userData.annotationLabel = true
      exportGroup.add(textMesh)
    }
  }

  const exporter = new GLTFExporter()
  const result = await exporter.parseAsync(exportGroup, { binary: true })
  if (!(result instanceof ArrayBuffer)) throw new Error('GLTFExporter did not return binary output')

  const blob = new Blob([result], { type: 'application/octet-stream' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

export async function exportOriginalWithAnnotations(
  originalBlob: Blob,
  originalFileName: string,
  annotations: ExportAnnotation[],
): Promise<void> {
  const [{ default: JSZip }] = await Promise.all([
    import('jszip') as Promise<typeof import('jszip')>,
  ])

  const zip = new JSZip()
  zip.file(originalFileName, originalBlob)

  const annJson = JSON.stringify(annotations, null, 2)
  const baseName = originalFileName.replace(/\.[^.]+$/, '')
  zip.file(`${baseName}_annotations.json`, annJson)

  const zipBlob = await zip.generateAsync({ type: 'blob' })
  const url = URL.createObjectURL(zipBlob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${baseName}_with_annotations.zip`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

export function exportAnnotationsAsJSON(
  annotations: ExportAnnotation[],
  fileName: string = 'annotations.json',
): void {
  const data = JSON.stringify(annotations, null, 2)
  const blob = new Blob([data], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

export async function exportModelAsHTML(
  group: THREE.Group,
  measurements: ExportMeasurements[] = [],
  annotations: ExportAnnotation[] = [],
  fileName: string = 'model-viewer.html',
  locale: string = 'en',
): Promise<void> {
  const [{ GLTFExporter }, { gzip }] = await Promise.all([
    import('three/examples/jsm/exporters/GLTFExporter') as Promise<typeof import('three/examples/jsm/exporters/GLTFExporter')>,
    import('pako') as Promise<typeof import('pako')>,
  ])

  const exporter = new GLTFExporter()
  const result = await exporter.parseAsync(group, { binary: true })
  if (!(result instanceof ArrayBuffer)) throw new Error('GLTFExporter did not return binary output')

  const compressed = gzip(new Uint8Array(result))
  const base64 = uint8ArrayToBase64(compressed)
  const html = generateHTML(base64, measurements, annotations, locale)

  const blob = new Blob([html], { type: 'text/html;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
